// videoEmbed — turn a user-pasted URL into a renderable form.
// Three cases handled, in priority order:
//
//   1. YouTube  (youtube.com/watch?v=ID, youtu.be/ID, youtube.com/embed/ID)
//      → iframe pointed at youtube.com/embed/ID
//   2. Vimeo    (vimeo.com/ID)
//      → iframe pointed at player.vimeo.com/video/ID
//   3. Direct   (.mp4 / .webm / .mov / anything else)
//      → native <video src> element
//
// Anything malformed falls back to the direct path — the <video>
// element shows its own error UI if the URL doesn't actually play.

export type VideoEmbed =
    | { kind: 'iframe'; src: string }
    | { kind: 'video'; src: string };

export const parseVideoEmbed = (raw: string): VideoEmbed => {
    const url = (raw || '').trim();
    if (!url) return { kind: 'video', src: '' };

    // YouTube — three URL shapes the user might paste.
    const yt = matchYouTubeId(url);
    if (yt) return { kind: 'iframe', src: `https://www.youtube.com/embed/${yt}` };

    // Vimeo — vimeo.com/<digits>
    const vm = url.match(/(?:player\.)?vimeo\.com\/(?:video\/)?(\d+)/);
    if (vm) return { kind: 'iframe', src: `https://player.vimeo.com/video/${vm[1]}` };

    return { kind: 'video', src: url };
};

const matchYouTubeId = (url: string): string | null => {
    // youtu.be/ID
    const short = url.match(/youtu\.be\/([\w-]{6,})/);
    if (short) return short[1];
    // youtube.com/watch?v=ID
    const watch = url.match(/[?&]v=([\w-]{6,})/);
    if (watch) return watch[1];
    // youtube.com/embed/ID
    const embed = url.match(/youtube\.com\/embed\/([\w-]{6,})/);
    if (embed) return embed[1];
    return null;
};
